import React, { useCallback, useMemo, useEffect, useRef } from "react";
import { Ref } from "semantic-ui-react";
import { MarketplaceAppSchemaFormModalProps } from "../types";
import CustomComponent from "./CustomComponent";
import FormInputField from "./form-fields/FormInputField";

const MarketplaceAppSchemaForm: React.FC<
  MarketplaceAppSchemaFormModalProps
> = ({ schema, formData, formErrors, onFormChange, isAdvanced }) => {
  const inputRefs = useRef<Record<string, HTMLElement | null>>({});

  const handleInputChange = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>, data: any) => {
      const value = data?.value || e.target.value;
      onFormChange(key, value);
    },
    [onFormChange],
  );

  const handleCustomComponentChange = useCallback(
    (key: string) => (data: { value: string | boolean | string[] }) => {
      onFormChange(key, data.value);
    },
    [onFormChange],
  );

  const inputTypeMapping: { [key: string]: string } = useMemo(
    () => ({
      integer: "number",
      password: "password",
      email: "email",
      url: "url",
    }),
    [],
  );

  const renderProperties = useMemo(() => {
    if (!schema?.properties) return null;

    const orderedKeys =
      (schema["ui:order"] as string[]) ||
      schema.required ||
      Object.keys(schema.properties);

    return orderedKeys.map((inputKey: string) => {
      const property = schema.properties[inputKey];
      if (!property) return null;
      const {
        title = inputKey,
        type,
        default: defaultValue,
        format,
        disabled,
        hidden,
        advancedModeView,
        pattern,
        description,
      } = property;

      // Show advanced fields only when appropriate.
      if (
        (isAdvanced && !advancedModeView) ||
        (!isAdvanced && advancedModeView)
      )
        return null;
      if (hidden) return null;

      const isRequired = schema.required?.includes(inputKey);
      const value = formData[inputKey] ?? defaultValue;
      const errorMessage = formErrors?.[inputKey]?.join(", ");

      let renderedField = null;
      if (format) {
        const commonProps = {
          region: formData.region,
          handleChange: handleCustomComponentChange(inputKey),
          inputKey,
          isRequired,
          value,
          disabled,
          hidden,
          formData,
          errorMessage,
          description,
        };
        renderedField = (
          <CustomComponent
            key={inputKey}
            inputKey={inputKey}
            title={title}
            value={value}
            format={format}
            commonProps={commonProps}
            schema={schema}
          />
        );
      } else {
        renderedField = (
          <FormInputField
            key={inputKey}
            inputKey={inputKey}
            title={title}
            value={value}
            isRequired={isRequired}
            errorMessage={errorMessage}
            inputType={inputTypeMapping[type] || "text"}
            onChange={handleInputChange}
            disabled={disabled}
            pattern={pattern}
            description={description}
          />
        );
      }

      const fieldContent = (
        <Ref
          key={inputKey}
          innerRef={(ref) => (inputRefs.current[inputKey] = ref)}
        >
          {renderedField}
        </Ref>
      );

      if (inputKey.endsWith("_name")) {
        return (
          <div key={inputKey} className="separator padding-top-30">
            {fieldContent}
          </div>
        );
      }
      return fieldContent;
    });
  }, [
    schema,
    formData,
    formErrors,
    handleCustomComponentChange,
    handleInputChange,
    inputTypeMapping,
    isAdvanced,
  ]);

  const initializeDefaultValues = useCallback(() => {
    if (!schema?.properties) return;
    const orderedKeys =
      (schema["ui:order"] as string[]) ||
      schema.required ||
      Object.keys(schema.properties);
    orderedKeys.forEach((key: string) => {
      const property = schema.properties[key];
      const defaultValue = property?.default;
      if (defaultValue !== undefined && formData[key] === undefined) {
        onFormChange(key, defaultValue);
      }
    });
  }, [schema, formData, onFormChange]);

  useEffect(() => {
    initializeDefaultValues();
  }, [initializeDefaultValues]);

  return <>{renderProperties}</>;
};

export default React.memo(MarketplaceAppSchemaForm);
